import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, ModalBody, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ArrowExchangeIcon } from 'assets/icon-arrow-exchange.svg';
import { ReactComponent as TranslateIcon } from 'assets/icon-translate.svg';

import { ModalHeader } from 'shared/components/modal/ModalHeaders';
import { TextField } from 'shared/components/form/Inputs';
import { Button } from 'shared/components/Buttons';
import MainSpinner from 'shared/components/MainSpinner';
import CustomSelect from 'shared/components/CustomSelect';
import AlertModal from 'shared/components/AlertModal';

import useApi from 'hooks/useApi';
import useLanguages from 'hooks/useLanguages';

import { showAlert, closeAlert } from 'redux/actions/alertActions';

import { ALERT_TYPE_ERROR, ALERT_TYPE_SUCCESS, emptyString } from 'shared/utils';
import { convertErrorsArrayToString } from 'shared/helpers/helper';
import { colors } from 'shared/defaultTheme';

import { CustomModalWrapper, TranslateLabel, H3 } from './styles';

const TranslateFormModal = ({ onCloseModal, refreshStateTranslateData, urlFragment }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cusNavId } = useSelector((state) => state.auth);
  const { mainColor } = useSelector((state) => state.theme);
  const documentState = useSelector((state) => state.documentState);
  const { statesTranslate } = documentState;
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const { customAreaApi } = useApi();
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [showSuccessSave, setShowSuccessSave] = useState();
  const [translateStates, setTranslateStates] = useState([]);
  const { languages } = useLanguages();

  const [{ loading: loadingPost }, postState] = customAreaApi(
    {
      method: 'POST',
    },
    { manual: true }
  );

  const onToggleSuccessSave = () => setShowSuccessSave((currentShow) => !currentShow);

  useEffect(() => {
    setTranslateStates(statesTranslate);
  }, [statesTranslate]);

  const onChangeLanguage = (e) => {
    const { target } = e;
    const { value } = target;
    setSelectedLanguage(value);
  };

  const findName = (currentState, codeLang, dataDefault) => {
    const findI18n = currentState.i18List.find((i18object) => i18object.i18Code === codeLang);
    if (findI18n) {
      return findI18n.i18Name;
    }
    if (dataDefault && !findI18n) {
      return currentState.i18List[0].i18Name;
    }
    return '';
  };

  const onChangeValue = (event, state) => {
    const { target } = event;
    const { value } = target ?? { value: '' };

    let newState = {};

    if (value && value.length > 0) {
      const i18Item = state.i18List.find((i18object) => i18object.i18Code === selectedLanguage);
      if (i18Item) {
        newState = {
          ...state,
          i18List: [
            ...state.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      } else {
        newState = {
          ...state,
          i18List: [
            ...state.i18List,
            {
              i18Code: selectedLanguage,
              i18name: value,
            },
          ],
        };
      }
    } else {
      newState = {
        ...state,
        i18List: [
          ...state.i18List.filter((i18object) => i18object.i18Code !== selectedLanguage),
          {
            i18Code: selectedLanguage,
            i18name: '',
          },
        ],
      };
    }

    setTranslateStates(
      translateStates.map((currentState) => {
        if (currentState.id === newState.id) {
          return newState;
        }
        return currentState;
      })
    );
  };

  const translate = async () => {
    try {
      const {
        data: { errors },
      } = await postState({
        url: `${window.API_V1}/${urlFragment}/${cusNavId}/modify`,
        data: translateStates,
      });
      if (errors.length) {
        throw new Error(convertErrorsArrayToString(errors));
      }
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_SUCCESS,
          alertMessage: t('general_success_message'),
        })
      );
      onToggleSuccessSave();
      refreshStateTranslateData();
    } catch (ex) {
      console.error('Error on translate States: ', ex);
      dispatch(
        showAlert({
          alertType: ALERT_TYPE_ERROR,
          alertMessage: t('general_error_message'),
        })
      );
    }
  };

  const onOkSuccessSave = () => {
    onToggleSuccessSave();
    dispatch(closeAlert());
  };

  const Title = () => (
    <Row className="row-title mb-2">
      <Col sm={12} className="d-flex align-items-end">
        <TranslateIcon width="30" />
        <H3 className="text-uppercase ml-2">{t('state_translate')}</H3>
      </Col>
    </Row>
  );
  return (
    <CustomModalWrapper className="translate-modal" isOpen toggle={onCloseModal} size="lg">
      <ModalHeader
        className="modal-header"
        backgroundColor={colors.transparent}
        onClose={onCloseModal}
      />
      <ModalBody>
        <Title />
        <Row className="remove-margin-horizontal mb-2">
          <Col className="d-flex flex-row justify-content-end" sm={12}>
            <CustomSelect
              defaultValue={
                emptyString(selectedLanguage) ? selectedDefaultLanguage : selectedLanguage
              }
              options={languages}
              onChange={onChangeLanguage}
            />
          </Col>
        </Row>
        <form className="form" noValidate>
          <Row className="remove-margin-horizontal mb-2">
            <Col sm={6}>
              <TranslateLabel color={mainColor}>{t('general_translate_text')}</TranslateLabel>
            </Col>
            <Col sm={1} />
            <Col sm={5}>
              <TranslateLabel color={mainColor}>{t('custom_folder_translation')}</TranslateLabel>
            </Col>
          </Row>
          {translateStates.length > 0 &&
            translateStates.map((state) => (
              <Row key={state.id} className="remove-margin-horizontal">
                <Col sm={6}>
                  <div className="form__form-group">
                    <TextField value={findName(state, selectedDefaultLanguage, true)} />
                  </div>
                </Col>
                <Col sm={1} className="d-flex align-items-center justify-content-center pb-3">
                  <ArrowExchangeIcon className="translate-icon" fill={mainColor} />
                </Col>
                <Col sm={5}>
                  <div className="form__form-group">
                    <TextField
                      value={findName(state, selectedLanguage || selectedDefaultLanguage, false)}
                      onChange={(event) => {
                        if (
                          !emptyString(selectedLanguage) &&
                          selectedLanguage !== selectedDefaultLanguage
                        ) {
                          onChangeValue(event, state);
                        }
                      }}
                    />
                  </div>
                </Col>
              </Row>
            ))}
          <Row className="w-100 remove-margin-horizontal">
            <Col className="d-flex flex-row justify-content-end" sm={12}>
              <Button
                className="text-uppercase mr-4"
                type="button"
                color="secondary"
                label={t('general_button_cancel')}
                onClick={onCloseModal}
              />
              <Button
                className="text-uppercase"
                type="button"
                color="primary"
                label={t('general_button_save')}
                onClick={translate}
              />
            </Col>
          </Row>
        </form>
        {loadingPost && <MainSpinner />}
      </ModalBody>
      <AlertModal
        type="success"
        isOpen={showSuccessSave}
        toggle={onOkSuccessSave}
        message={t('general_success_message')}
        okText={t('general_button_ok')}
        onOk={onOkSuccessSave}
      />
    </CustomModalWrapper>
  );
};

TranslateFormModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  refreshStateTranslateData: PropTypes.func.isRequired,
  urlFragment: PropTypes.string.isRequired,
};

export default TranslateFormModal;
